import React, { createContext, useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import { useDialog } from "../components/Modal/DialogContext";
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resetAllUserScopedState } from "../utils/resetUserScopedState";
interface AuthContextType {
  user: string | null;
  authLoading: boolean;
  bannerAnnouncement: any | null;
  closeBannerAnnouncement: () => Promise<void>;
  dismissBannerAnnouncement: () => Promise<void>;
  trackBannerAnnouncementShown: () => Promise<void>;
  trackBannerAnnouncementClick: (cta: 1 | 2) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<void>;
  registerWithTicket: (
    email: string,
    password: string,
    code: string,
    consents?: { consent_type: string; version: string; source: string; contract_id?: number }[]
  ) => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  openLoginDialog: () => void;
  openRegisterDialog: () => void;
  openChangePasswordDialog: () => void;
  logout: (fromBroadcast?: boolean) => void;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
}


export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [bannerAnnouncement, setBannerAnnouncement] = useState<any | null>(null);

  // Dialog-Steuerung
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
  const { settings: siteSettings, loading: isSiteSettingsLoading, reload: reloadSiteSettings } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;;
  const [username, setUsername] = useState("");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

const openDialog = useDialog();


  const fetchAnnouncements = async (accessToken?: string, options?: { loginEvent?: boolean }) => {
    try {
      const res = await axios.get("/api/announcements/active/", {
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...(options?.loginEvent ? { "X-Announcement-Login-Event": "1" } : {}),
        },
      });

      const announcements = Array.isArray(res.data) ? res.data : [];
      const modalAnnouncement = announcements.find(
        (announcement: any) => (announcement.placement || "modal") === "modal"
      );
      const nextBannerAnnouncement = announcements.find(
        (announcement: any) => announcement.placement === "banner"
      );
      setBannerAnnouncement(nextBannerAnnouncement ?? null);

      if (modalAnnouncement) {
        const announcement = modalAnnouncement;

        openDialog({
          type: "announcement",
          props: {
            announcement,
            onShown: () =>
              axios.post(`/api/announcements/${announcement.id}/shown/`),
            onCloseAnnouncement: () =>
              axios.post(`/api/announcements/${announcement.id}/close/`),
            onDismiss: () =>
              axios.post(`/api/announcements/${announcement.id}/dismiss/`),
            onSnooze: () =>
              axios.post(`/api/announcements/${announcement.id}/snooze/`, {
                minutes: 60,
              }),
            onCtaClick: (cta: 1 | 2) =>
              axios.post(
                `/api/announcements/${announcement.id}/click/${cta}/`
              ),
          },
        });
      }
    } catch (err) {
      if (logLevel >= 1) console.error("Fehler beim Laden der Ankündigungen:", err);
    }
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded: { email: string; exp: number } = jwtDecode(token);
        if (decoded.exp > Date.now() / 1000) {
          setUser(decoded.email);
          fetchAnnouncements(token);
        } else {
          logout();
        }
      }
    } catch {
      logout();
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem("token");

      if (token) {
        try {
          const decoded = jwtDecode<{ exp: number }>(token);
          const currentTime = Math.floor(Date.now() / 1000);

          if (decoded.exp < currentTime) {
            logout(); // 🔹 Token abgelaufen → Abmelden
            alert("Ihre Sitzung ist abgelaufen. Bitte loggen Sie sich erneut ein.");
          }
        } catch (error) {
          if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Dekodieren des Tokens:", error);
          logout(); // 🔹 Falls das Token beschädigt ist, abmelden
        }
      }
    }, 6000); // 🔹 Alle 6 Sekunden prüfen

    return () => clearInterval(interval); // 🔹 Beim Verlassen aufräumen
  }, []);

  useEffect(() => {
    if (!user) return;

    const sendHeartbeat = async () => {
      if (document.visibilityState === "hidden") return;

      try {
        await axios.get("/auth/api/heartbeat/", {
          headers: { "Cache-Control": "no-store" },
        });
        if (!isSiteSettingsLoading && logLevel >= 3) {
          console.log("Heartbeat gesendet");
        }
      } catch (error) {
        if (!isSiteSettingsLoading && logLevel >= 1) {
          console.error("Heartbeat fehlgeschlagen:", error);
        }
      }
    };

    sendHeartbeat(); // direkt beim Login/Reload
    const interval = setInterval(sendHeartbeat, 60000); // alle 60s (TTL ist 120s)

    return () => clearInterval(interval);
  }, [user, isSiteSettingsLoading, logLevel]);

  // 🔹 BroadcastChannel-Überwachung für Logout in anderen Tabs
  useEffect(() => {
    const channel = new BroadcastChannel("auth_channel");

    channel.onmessage = (event) => {
      if (event.data === "logout") {
        logout(true); // ✅ Jetzt wird KEINE neue Nachricht gesendet
        //alert("Ihre Sitzung wurde in einem anderen Tab beendet.");
      }
    };

    return () => {
      channel.close();
    };
  }, []);


  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post("/auth/api/token/", { email, password });
      const { access, refresh } = response.data;
      localStorage.setItem("token", access);
      localStorage.setItem("refreshToken", refresh);
      localStorage.setItem("forceFreshUserProfile", "1");
      resetAllUserScopedState();
      const decoded: { email: string } = jwtDecode(access);
      setUser(decoded.email);
      await reloadSiteSettings();
      await fetchAnnouncements(access, { loginEvent: true });
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Login fehlgeschlagen:", error);
      throw error; // Weiterwerfen, damit LoginPage Fehler anzeigen kann
    }
  };

  const logout = (fromBroadcast = false) => {
    resetAllUserScopedState();
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setUser(null);
    setBannerAnnouncement(null);
    // alert("Sie wurden erfolgreich abgemeldet.");

    if (!fromBroadcast) {
      const channel = new BroadcastChannel("auth_channel");
      channel.postMessage("logout");
      channel.close();
    }
  };

  const register = async (email: string, password: string, fullName: string) => {
    try {
      await axios.post("/auth/api/register/", { email, password, fullName });
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) {
        console.error("Registrierung fehlgeschlagen:", error);
      }
      throw error; // 👈 WICHTIG: Fehler weitergeben
    }
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        logout();
        return;
      }

      const response = await axios.post("/auth/api/token/refresh/", {
        refresh: refreshToken,
      });
      const { access } = response.data;
      localStorage.setItem("token", access);
      await fetchAnnouncements(access);
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) {
        console.error("Token-Refresh fehlgeschlagen:", error);
      }
      logout();
      throw error;
    }
  };


  const registerWithTicket = async (
    email: string,
    password: string,
    code: string,
    consents?: { consent_type: string; version: string; source: string; contract_id?: number }[]
  ) => {
    try {
      await axios.post("auth/api/register-with-ticket/", { email, password, code});
      if (consents && consents.length > 0) {
        try {
          await Promise.all(consents.map(consent =>
            fetch("/api/log-consent/", {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
                body: JSON.stringify({
                ...consent,
                email: email
              })
            })
          ));
        } catch (error) {
          if (!isSiteSettingsLoading && logLevel >= 1) {
            console.error("Fehler beim Loggen der Einwilligungen:", error);
          }
        }
      }
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) {
        console.error("Registrierung fehlgeschlagen:", error);
      }
      throw error; // 👈 WICHTIG: Fehler weitergeben
    }
  };

  const changePassword = async () => {

    if (newPassword !== confirmNewPassword) {
      alert("Die neuen Passwörter stimmen nicht überein.");
      return;
    }

    try {
      await axios.post("/auth/api/change-password/", { old_password: oldPassword, new_password: newPassword });
      alert("Passwort erfolgreich geändert.");
      setIsChangePasswordDialogOpen(false);
      setOldPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Ändern des Passworts:", error);
      alert("Fehler beim Ändern des Passworts. Bitte erneut versuchen.");
    }
  };
  const requestPasswordReset = async (email: string) => {
    try {
      const response = await fetch("/auth/api/request-password-reset/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ email })
      });

      const contentType = response.headers.get("content-type");
      let data: any = {};

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        throw new Error(`Serverantwort ist kein JSON: ${text.slice(0, 100)}`);
      }

      if (!response.ok) {
        throw new Error(data.error || "Unbekannter Fehler");
      }

      //alert(data.message || "E-Mail zum Zurücksetzen wurde gesendet.");
    } catch (err: any) {
      if (!isSiteSettingsLoading && logLevel >= 1)
        console.error("Fehler beim Zurücksetzen des Passworts:", err);
      throw err;
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      const response = await fetch("/auth/api/reset-password/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ token, new_password: newPassword })
      });

      const contentType = response.headers.get("content-type");
      let data: any = {};

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        throw new Error(`Serverantwort ist kein JSON: ${text.slice(0, 100)}`);
      }

      if (!response.ok) {
        throw new Error(data.error || "Unbekannter Fehler");
      }

      //alert(data.message || "Passwort wurde erfolgreich zurückgesetzt.");
    } catch (err: any) {
      if (!isSiteSettingsLoading && logLevel >= 1)
        console.error("Fehler beim Zurücksetzen des Passworts:", err);
      throw err;
    }
  };

  // Konto löschen Funktion
  const deleteAccount = async () => {
    try {
        await axios.delete("/auth/api/delete-account/");
        logout(); // Token löschen, Benutzer abmelden
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) {
        console.error("Fehler beim Löschen des Kontos:", error);
      }
      throw error;
    }
  };

  const closeBannerAnnouncement = async () => {
    if (!bannerAnnouncement) return;
    await axios.post(`/api/announcements/${bannerAnnouncement.id}/close/`);
    setBannerAnnouncement(null);
  };

  const dismissBannerAnnouncement = async () => {
    if (!bannerAnnouncement) return;
    await axios.post(`/api/announcements/${bannerAnnouncement.id}/dismiss/`);
    setBannerAnnouncement(null);
  };

  const trackBannerAnnouncementShown = async () => {
    if (!bannerAnnouncement) return;
    await axios.post(`/api/announcements/${bannerAnnouncement.id}/shown/`);
  };

  const trackBannerAnnouncementClick = async (cta: 1 | 2) => {
    if (!bannerAnnouncement) return;
    const response = await axios.post(`/api/announcements/${bannerAnnouncement.id}/click/${cta}/`);
    if (response.data?.dismissed) {
      setBannerAnnouncement(null);
    }
    return response.data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        authLoading,
        bannerAnnouncement,
        closeBannerAnnouncement,
        dismissBannerAnnouncement,
        trackBannerAnnouncementShown,
        trackBannerAnnouncementClick,
        login,
        logout,
        register,
        registerWithTicket,
        refreshAccessToken,
        openLoginDialog: () => setIsLoginDialogOpen(true),
        openRegisterDialog: () => setIsRegisterDialogOpen(true),
        openChangePasswordDialog: () => setIsChangePasswordDialogOpen(true),
        requestPasswordReset,
        resetPassword,
        deleteAccount,
      }}
    >
      {children}

      {/* Login-Dialog */}

      {/* Registrierungs-Dialog */}
      <Dialog open={isRegisterDialogOpen} onClose={() => setIsRegisterDialogOpen(false)}>
        <DialogTitle>Registrieren</DialogTitle>
        <DialogContent>
          <TextField label="Benutzername" fullWidth value={username} onChange={(e) => setUsername(e.target.value)} margin="normal" />

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRegisterDialogOpen(false)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      {/* Passwort ändern Dialog */}
      <Dialog open={isChangePasswordDialogOpen} onClose={() => setIsChangePasswordDialogOpen(false)}>
        <DialogTitle>Passwort ändern</DialogTitle>
        <DialogContent>
          <TextField label="Altes Passwort" type="password" fullWidth value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} margin="normal" />
          <TextField label="Neues Passwort" type="password" fullWidth value={newPassword} onChange={(e) => setNewPassword(e.target.value)} margin="normal" />
          <TextField label="Neues Passwort bestätigen" type="password" fullWidth value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} margin="normal" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsChangePasswordDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={changePassword}>Passwort ändern</Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};
